import React from 'react'
import type { Card, Ship } from './engine'
import { nextId, countProgramSlots } from './engine'
import { getCardDef } from './cardDefs'
import { PirateCard, type TargetOption } from './PirateCard'
import { getDrag, registerDropZone } from './dnd'
import type { ActionType, ControlType, CardType } from './engine'

const MAX_CARDS = 3

interface ShipBoardProps {
  ship: Ship
  editable: boolean
  /** Enemy ships available as attack targets. */
  targets: TargetOption[]
  onProgramChange: (program: Card[]) => void
  /** Animation highlights. */
  highlightShipId?: string | null
  highlightCardId?: string | null
  /** Accent colour for the fleet this ship belongs to. */
  accent?: string
  /** When false, control card params are shown as static text (activity mode). */
  allowParamEdit?: boolean
}

/** Build a fresh card instance from a dragged palette type. */
function makeCardFromType(type: CardType, kind: 'action' | 'control', param?: number): Card {
  if (kind === 'action') {
    return { id: nextId('card'), kind: 'action', action: type as ActionType, targetShipId: null }
  }
  const def = getCardDef(type)
  return {
    id: nextId('card'),
    kind: 'control',
    control: type as ControlType,
    param: param ?? def.paramOptions?.[0] ?? 0,
    body: [],
    ifBody: [],
    elseBody: [],
  }
}

/** One ship: potato gauge + its stack of up to 3 program cards. */
export function ShipBoard({
  ship,
  editable,
  targets,
  onProgramChange,
  highlightShipId,
  highlightCardId,
  accent = '#0ea5e9',
  allowParamEdit = true,
}: ShipBoardProps) {
  const [over, setOver] = React.useState(false)
  const dead = ship.potatoes <= 0
  const usedSlots = countProgramSlots(ship.program)
  // A loop card at the top level locks the program — no sibling cards allowed.
  // All remaining slots must go inside the loop's body.
  const hasTopLevelLoop = ship.program.some(
    (c) => c.kind === 'control' && c.control !== 'ifelse',
  )
  const full = usedSlots >= MAX_CARDS || hasTopLevelLoop
  const highlighted = highlightShipId === ship.id

  const dropZoneRef = React.useRef<HTMLDivElement>(null)
  const dropHandlerRef = React.useRef<() => void>(() => {})
  dropHandlerRef.current = () => {
    const payload = getDrag()
    if (!editable || !payload || full) return
    onProgramChange([...ship.program, makeCardFromType(payload.type, payload.kind, payload.param)])
    setOver(false)
  }
  React.useEffect(() => {
    const el = dropZoneRef.current
    if (!el) return
    return registerDropZone(el, () => dropHandlerRef.current())
  }, [])

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    dropHandlerRef.current()
  }

  const accept = (e: React.DragEvent) => {
    const payload = getDrag()
    if (editable && payload && !full) {
      e.preventDefault()
      setOver(true)
    }
  }

  const updateCard = (idx: number, updated: Card) => {
    const next = ship.program.slice()
    next[idx] = updated
    onProgramChange(next)
  }

  const removeCard = (idx: number) => {
    onProgramChange(ship.program.filter((_, i) => i !== idx))
  }

  return (
    <div
      style={{
        border: `2px solid ${highlighted ? accent : 'var(--pp-border)'}`,
        borderRadius: 14,
        padding: 12,
        background: dead ? 'var(--pp-surface-slot)' : 'var(--pp-surface)',
        opacity: dead ? 0.6 : 1,
        boxShadow: highlighted ? `0 0 0 3px ${accent}55` : 'none',
        transition: 'box-shadow 150ms ease, opacity 200ms ease',
        minWidth: 200,
      }}
    >
      {/* Ship header + potato gauge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 22 }}>{dead ? '🌊' : '⛵'}</span>
        <span style={{ fontWeight: 800, fontSize: 14, color: 'var(--pp-text)' }}>{ship.name}</span>
        <span style={{ marginLeft: 'auto', fontWeight: 800, fontSize: 13, color: dead ? 'var(--pp-text-muted)' : 'var(--color-warning)' }}>
          {ship.potatoes}/{ship.maxPotatoes} 🥔
        </span>
      </div>
      <PotatoGauge potatoes={ship.potatoes} max={ship.maxPotatoes} accent={accent} />

      {/* Program slots */}
      <div
        ref={dropZoneRef}
        onDragOver={accept}
        onDragEnter={accept}
        onDragLeave={() => setOver(false)}
        onDrop={handleDrop}
        style={{
          marginTop: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          padding: 8,
          borderRadius: 10,
          minHeight: 70,
          background: over ? 'var(--color-primary-subtle)' : 'var(--pp-surface-slot)',
          border: `2px dashed ${over ? 'var(--pp-action-accent)' : 'var(--pp-border)'}`,
        }}
      >
        {ship.program.length === 0 && (
          <div style={{ fontSize: 11, color: 'var(--pp-text-muted)', textAlign: 'center', padding: '14px 0' }}>
            {editable ? 'Drop up to 3 cards here' : 'No cards'}
          </div>
        )}
        {ship.program.map((card, idx) => (
          <PirateCard
            key={card.id}
            card={card}
            editable={editable}
            targets={targets}
            onChange={(c) => updateCard(idx, c)}
            onRemove={() => removeCard(idx)}
            highlightCardId={highlightCardId}
            shipSlotsUsed={usedSlots}
            allowParamEdit={allowParamEdit}
          />
        ))}
        {editable && full && (
          <div style={{ fontSize: 10, color: 'var(--pp-text-muted)', textAlign: 'center' }}>
            {hasTopLevelLoop ? 'Drop cards inside the loop ↑' : `Card slots full (${usedSlots}/3)`}
          </div>
        )}
      </div>
    </div>
  )
}

function PotatoGauge({ potatoes, max, accent }: { potatoes: number; max: number; accent: string }) {
  return (
    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
      {Array.from({ length: max }).map((_, i) => (
        <span
          key={i}
          style={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: i < potatoes ? accent : 'var(--pp-border)',
            transition: 'background 150ms ease',
          }}
        />
      ))}
    </div>
  )
}
